<template>
    <Modal ref="modal" title="Share Playlist" size="large">
        <!-- Tabs Header -->
        <div class="border-b border-gray-200 dark:border-gray-700 mb-6">
            <ul class="flex flex-wrap -mb-px text-sm font-medium text-center">
                <li class="mr-2">
                    <button 
                        @click="activeTab = 'share'" 
                        class="inline-flex items-center gap-2 p-3 border-b-2 rounded-t-lg transition-colors cursor-pointer text-xs md:text-sm"
                        :class="[
                            activeTab === 'share' 
                                ? 'text-indigo-600 border-indigo-600 dark:text-indigo-500 dark:border-indigo-500 font-semibold' 
                                : 'text-gray-500 border-transparent hover:text-gray-600 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                        ]"
                    >
                        <ShareIcon class="size-4" />
                        Share Options
                    </button>
                </li>
                <li>
                    <button 
                        @click="activeTab = 'realtime'" 
                        class="inline-flex items-center gap-2 p-3 border-b-2 rounded-t-lg transition-colors cursor-pointer text-xs md:text-sm"
                        :class="[
                            activeTab === 'realtime' 
                                ? 'text-indigo-600 border-indigo-600 dark:text-indigo-500 dark:border-indigo-500 font-semibold' 
                                : 'text-gray-500 border-transparent hover:text-gray-600 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                        ]"
                    >
                        <SignalIcon class="size-4" />
                        Realtime Control
                    </button>
                </li>
            </ul>
        </div>

        <!-- Tab 1: Share Options -->
        <div v-show="activeTab === 'share'" class="grid grid-cols-1 md:grid-cols-2 gap-8 divide-y md:divide-y-0 md:divide-x divide-gray-200 dark:divide-gray-600">
            
            <!-- Left Column: Search contacts and actions -->
            <div class="space-y-6">
                <div>
                    <label for="search-contacts" class="block mb-2 text-sm font-semibold text-gray-900 dark:text-white">
                        Find contacts to share playlist with
                    </label>
                    <div class="relative">
                        <div class="absolute inset-y-0 start-0 flex items-center ps-3 pointer-events-none">
                            <svg class="w-4 h-4 text-gray-500 dark:text-gray-400" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z"/>
                            </svg>
                        </div>
                        <input 
                            type="search" 
                            id="search-contacts" 
                            class="block w-full p-2.5 ps-10 text-sm text-gray-900 border border-gray-300 dark:border-gray-600 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 focus:border-indigo-500 dark:bg-gray-700 dark:placeholder-gray-400 dark:text-white" 
                            placeholder="Type at least 2 characters to search..." 
                            v-model="searchText"
                        />
                    </div>

                    <!-- Search Results Display Area (Directly below search input) -->
                    <div class="mt-4 min-h-[100px] relative transition-all duration-300">
                        <div v-if="contactLoading" class="py-4">
                            <Loading class="w-full"/>
                        </div>
                        
                        <!-- Empty Search Hint -->
                        <div class="text-sm font-normal text-gray-500 dark:text-gray-400 text-center py-4"
                             v-if="!contactLoading && filteredContactResults.length === 0 && searchText.length < 2">
                            Search for users or teams by name or email
                        </div>
                        
                        <!-- Search Results and Error Messages -->
                        <div v-if="searchText.length >= 2 && !contactLoading" class="space-y-2 max-h-[250px] overflow-y-auto pr-1">
                            <div class="text-sm font-medium text-red-600 dark:text-red-400 text-center py-4" v-if="contactErrorMessage && !contactLoading">
                                {{ contactErrorMessage }}
                            </div>
                            
                            <!-- Search Result Cards -->
                            <div v-for="contact in filteredContactResults" :key="contactKey(contact)" 
                                 class="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 border border-gray-100 dark:border-gray-700/50 rounded-xl transition-all hover:bg-gray-100/75 dark:hover:bg-gray-700/50">
                                <div class="flex items-center min-w-0 mr-4">
                                    <div class="flex-shrink-0">
                                        <!-- Avatar Image or Icon Badge -->
                                        <img v-if="contact.avatarUrl" :src="contact.avatarUrl" width="40" height="40" loading="lazy" class="w-10 h-10 rounded-full object-cover border border-gray-200 dark:border-gray-700" :alt="contactLabel(contact)">
                                        <div v-else-if="isTeam(contact)" class="w-10 h-10 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300 flex items-center justify-center font-bold">
                                            <svg class="w-5 h-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                                                <path stroke-linecap="round" stroke-linejoin="round" d="M18 18.72a9.094 9.094 0 0 0 3.741-.479 3 3 0 0 0-4.682-2.72m.94 3.198.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0 1 12 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 0 1 6 18.719m12 0a5.971 5.971 0 0 0-.941-3.197m0 0A5.995 5.995 0 0 0 12 12.75a5.995 5.995 0 0 0-5.058 2.772m0 0a3 3 0 0 0-4.681 2.72 8.986 8.986 0 0 0 3.74.477m.94-3.197a5.971 5.971 0 0 0-.94 3.197M15 6.75a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm6 3a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Zm-13.5 0a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Z" />
                                            </svg>
                                        </div>
                                        <div v-else class="w-10 h-10 rounded-full bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300 flex items-center justify-center font-bold text-sm tracking-wider">
                                            {{ getInitials(contactLabel(contact)) }}
                                        </div>
                                    </div>
                                    <div class="ml-3 min-w-0">
                                        <p class="text-sm font-semibold text-gray-900 dark:text-white truncate">{{ contactNameOnly(contact) }}</p>
                                        <p v-if="!isTeam(contact)" class="text-xs text-gray-500 dark:text-gray-400 truncate">{{ contact.email }}</p>
                                        <p v-else class="text-xs text-gray-500 dark:text-gray-400 truncate">{{ contact.memberCount || (contact.members && contact.members.length) || 0 }} members</p>
                                    </div>
                                </div>
                                <Button @click="handleAddContact(contact)" class="!ms-0">
                                    Share
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>

                <Divider>or</Divider>

                <!-- Share Link Section -->
                <div>
                    <Button @click="generateShareLink" class="w-full !ms-0 justify-center">
                        Generate Share Link
                    </Button>

                    <!-- Share Link Display -->
                    <div v-if="shareModalStore.state.shareLink" class="mt-4">
                        <div class="flex">
                            <input type="text" 
                                   class="flex-1 bg-gray-50 dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white text-sm rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 focus:border-indigo-500 block w-full p-2.5 dark:placeholder-gray-400" 
                                   :value="shareModalStore.state.shareLink" 
                                   readonly>
                            <Button @click="copyToClipboard" class="!ms-2">
                                Copy
                            </Button>
                        </div>
                    </div>
                </div>

                <!-- Loading State -->
                <Loading v-if="shareModalStore.state.loadingLink" class="w-full"/>

                <!-- Error/Success Message -->
                <div v-if="shareModalStore.state.message" 
                    :class="{'text-red-600 dark:text-red-400': shareModalStore.state.error, 'text-green-600 dark:text-green-400': !shareModalStore.state.error}"
                    class="mt-4 text-sm font-medium text-center">
                    {{ shareModalStore.state.message }}
                </div>
            </div>

            <!-- Right Column: Shared Entities List -->
            <div class="space-y-6 md:ps-8 pt-6 md:pt-0">
                <SharedEntitiesList 
                    :shared-users="shareModalStore.state.sharedUsers"
                    :shared-teams="shareModalStore.state.sharedTeams"
                    @update-role="updateRole"
                    @remove-user="removeUser"
                    @remove-team="removeTeam"
                />
            </div>
            
        </div>

        <!-- Tab 2: Realtime Control -->
        <div v-show="activeTab === 'realtime'">
            <RealtimeControlSelector 
                :shared-users="shareModalStore.state.sharedUsers"
                :realtime-user-id="realtimeUserId"
                :playlist-owner-id="playlistOwnerId"
                :playlist-owner-name="playlistOwnerName"
                :playlist-owner-avatar-url="playlistOwnerAvatarUrl"
                :can-manage-realtime="canManageRealtime"
                @change-realtime-user="changeRealtimeUser"
            />
        </div>
    </Modal>
</template>

<script setup>
import { ref, computed, defineExpose, onMounted, watch } from "vue";
import shareModalStore from "@/store/share.modal.store";
import playlistStore from "@/store/playlist.store";
import userStore from "@/store/user.store";
import Modal from "../common/TriadModal.vue";
import SharedEntitiesList from "./SharedEntitiesList.vue";
import RealtimeControlSelector from "./RealtimeControlSelector.vue";
import Loading from '@/components/TriadLoad.vue';
import toastStore from "@/store/toast.store";
import playlistService from "@/service/playlist.service";
import dbService from "@/service/db.service";
import { realtimeService } from "@/service/realtime.service";
import { appEvents } from '@/store/app.events';
import { ShareIcon, SignalIcon } from '@heroicons/vue/24/outline';

const modal = ref(null);
const activeTab = ref('share');
const localPlaylist = ref(null);

const fetchCurrentPlaylist = async (playlistId) => {
    if (!playlistId) return;
    try {
        // cache:'no-store' bypassa o SW (StaleWhileRevalidate) para o modal sempre ver o designado atual
        const res = await fetch(`/api/playlist/${playlistId}`, {
            headers: { 'Content-Type': 'application/json' },
            cache: 'no-store'
        });
        if (res.ok) {
            const data = await res.json();
            localPlaylist.value = data;
            if (!playlistStore.state.playlist || (playlistStore.state.playlist.id !== playlistId && playlistStore.state.playlist._id !== playlistId)) {
                playlistStore.state.playlist = data;
            } else {
                playlistStore.commit('PATCH_REALTIME_USER', {
                    realtime_user_id: data.realtime_user_id,
                    realtime_user: data.realtime_user,
                });
            }
        }
    } catch (e) {
        console.error('Error fetching playlist details for share modal:', e);
    }
};

watch(() => shareModalStore.state.currentPlaylistId, (newId) => {
    if (newId) {
        // Abre direto na aba pedida (ex: badge "Control" -> realtime-control).
        if (shareModalStore.state.initialTab === 'realtime') {
            activeTab.value = 'realtime';
        }
        fetchCurrentPlaylist(newId);
    } else {
        localPlaylist.value = null;
        activeTab.value = 'share';
    }
}, { immediate: true });

// Espelha o designado da store no modal: o broadcast realtime_user_changed faz PATCH
// só na store — sem isso o localPlaylist stale sombreava a store no computed realtimeUserId
// e o modal continuava marcando Owner mesmo com o controle já em cto.developer@gmail.com.
watch(() => playlistStore.state.playlist?.realtime_user_id, (newVal) => {
    if (localPlaylist.value && newVal !== undefined && localPlaylist.value.realtime_user_id !== newVal) {
        localPlaylist.value.realtime_user_id = newVal;
    }
});
watch(() => playlistStore.state.playlist?.realtime_user, (newVal) => {
    if (localPlaylist.value && newVal !== undefined) {
        localPlaylist.value.realtime_user = newVal;
    }
}, { deep: true });

// Refetch ao abrir o modal para nunca exibir designado stale do fetch anterior
watch(() => shareModalStore.state.open, (isOpen) => {
    if (isOpen && shareModalStore.state.currentPlaylistId) {
        fetchCurrentPlaylist(shareModalStore.state.currentPlaylistId);
    }
});

const activePlaylist = computed(() => localPlaylist.value || playlistStore.state.playlist);

const playlistOwnerId = computed(() => {
    return activePlaylist.value?.userId
        || activePlaylist.value?.user_id
        || activePlaylist.value?.user?._id
        || activePlaylist.value?.user?.id
        || null;
});

const playlistOwnerName = computed(() => {
    const user = activePlaylist.value?.user;
    if (!user) return 'Owner';
    return user.fullName || user.full_name || user.name || user.username || user.email || 'Owner';
});

const playlistOwnerAvatarUrl = computed(() => {
    const user = activePlaylist.value?.user;
    return user?.avatarUrl || user?.avatar_url || null;
});

const realtimeUserId = computed(() => {
    if (localPlaylist.value && localPlaylist.value.realtime_user_id !== undefined) {
        return localPlaylist.value.realtime_user_id ?? null;
    }
    const raw = playlistStore.state.playlist?.realtime_user_id;
    if (raw !== undefined && raw !== null) return raw;
    const camel = playlistStore.state.playlist?.realtimeUserId;
    return camel ?? null;
});

const isPlaylistOwner = computed(() => {
    const currentUserId = userStore.state.userInfo?.id || userStore.state.userInfo?._id;
    const ownerId = playlistOwnerId.value;
    return !!(activePlaylist.value
        && currentUserId
        && ownerId
        && String(ownerId) === String(currentUserId));
});

const isPlaylistEditor = computed(() => {
    return activePlaylist.value
        && activePlaylist.value.permissions
        && (activePlaylist.value.permissions.includes('editor') || activePlaylist.value.permissions.includes('owner'));
});

const canManageRealtime = computed(() => {
    const userInfo = userStore.state.userInfo;
    const isAdmin = userInfo?.role === 'admin' || userInfo?.isAdmin || userInfo?.membershipTier === 'admin_tier' || userInfo?.membershipTier === 'admin_membership';
    return isPlaylistOwner.value || isPlaylistEditor.value || isAdmin;
});

// Monta payload mínimo do novo controller para o broadcast (toast/badge dos receptores).
const buildRealtimeUserPayload = (userId) => {
    if (!userId) return null;
    const ownerId = playlistOwnerId.value;
    if (ownerId && String(ownerId) === String(userId)) {
        const u = activePlaylist.value?.user || {};
        return {
            id: ownerId, _id: ownerId,
            full_name: playlistOwnerName.value,
            username: u.username || u.email || null,
            email: u.email || null
        };
    }
    const su = shareModalStore.state.sharedUsers.find(x => String(x.id || x._id) === String(userId));
    if (su) {
        return {
            id: su.id || su._id, _id: su._id || su.id,
            full_name: su.name || su.fullName || su.full_name || su.username || su.email,
            username: su.username || su.email || null,
            email: su.email || null,
            avatarUrl: su.avatarUrl || su.avatar_url || null
        };
    }
    return null;
};

const publishRealtimeUserChanged = (playlistId, newId, rUser) => {
    try {
        const cUid = userStore.state.userInfo?.id || userStore.state.userInfo?._id;
        realtimeService.publish(`playlist::room::${playlistId}`, 'realtime-user-changed', {
            playlistId,
            realtime_user_id: newId,
            realtime_user: rUser,
            realtime_user_changed: true,
            ts: Date.now(),
            sender: cUid,
            sender_client_id: window.myClientId || null
        }).catch(() => {});
    } catch (e) { /* best-effort, ignore */ }
};

const applyRealtimeUserLocal = (playlistId, rid, ru) => {
    if (localPlaylist.value) {
        localPlaylist.value.realtime_user_id = rid;
        if (ru !== null && ru !== undefined) localPlaylist.value.realtime_user = ru;
    }
    // Via mutation para garantir reatividade do badge/canControl
    playlistStore.commit('PATCH_REALTIME_USER', {
        realtime_user_id: rid,
        realtime_user: ru,
    });
    if (playlistId) {
        const cacheKey = playlistService.PLAYLIST_CACHE_KEY.replace("[id]", playlistId);
        playlistService.clearCache(cacheKey);
        dbService.removeItem(cacheKey).catch(() => {});
        // Endpoint de designation: evita SW stale no reload seguinte (playlistUpdated -> load force:true)
        playlistService.invalidateServiceWorkerCache(playlistId);
    }
};

const changeRealtimeUser = async (newRealtimeUserId) => {
    const playlistId = shareModalStore.state.currentPlaylistId || playlistStore.state.playlist?.id || playlistStore.state.playlist?._id;
    console.log('[TriadModalShare changeRealtimeUser] playlistId:', playlistId, 'newRealtimeUserId:', newRealtimeUserId);
    if (!playlistId) {
        toastStore.dispatch('addMessage', {
            message: 'Playlist ID not found',
            icon: 'error',
            ttl: 3000
        });
        return;
    }
    const rUser = buildRealtimeUserPayload(newRealtimeUserId);
    // Sessão primeiro (Fase 2b: vale offline): PATCH local + broadcast transport.
    // O PUT persiste no banco quando há backend; sem ele, fica sessão + sync no reconnect.
    applyRealtimeUserLocal(playlistId, newRealtimeUserId, rUser);
    publishRealtimeUserChanged(playlistId, newRealtimeUserId, rUser);
    toastStore.dispatch('addMessage', {
        message: 'Realtime controller updated successfully',
        icon: 'success',
        ttl: 3000
    });
    try {
        const response = await fetch(`/api/playlist/${playlistId}`, {
            method: 'PUT',
            body: JSON.stringify({
                realtime_user_id: newRealtimeUserId
            }),
            headers: {
                'Content-Type': 'application/json'
            }
        });
        console.log('[TriadModalShare changeRealtimeUser] response status:', response.status);
        if (response.ok) {
            const data = await response.json().catch(() => ({}));
            const freshId = data.realtime_user_id !== undefined ? data.realtime_user_id : newRealtimeUserId;
            const freshUser = data.realtime_user !== undefined ? data.realtime_user : rUser;
            applyRealtimeUserLocal(playlistId, freshId, freshUser);
            try { localStorage.removeItem(`triad_pending_designation::${playlistId}`); } catch (e) { /* best-effort, ignore */ }
            appEvents.emit('playlistUpdated', playlistId);
        } else {
            const errData = await response.json().catch(() => ({}));
            console.error('[TriadModalShare changeRealtimeUser] persist failed, session-only:', errData);
            // Sem backend (palco offline): mantém sessão e agenda sync no reconnect.
            // NÃO emite playlistUpdated (reload apagaria o PATCH otimista sem cache/banco).
            try {
                localStorage.setItem(`triad_pending_designation::${playlistId}`, JSON.stringify({ realtime_user_id: newRealtimeUserId }));
            } catch (e) { /* best-effort, ignore */ }
        }
    } catch (err) {
        console.error('[TriadModalShare changeRealtimeUser] persist failed, session-only:', err);
        try {
            localStorage.setItem(`triad_pending_designation::${playlistId}`, JSON.stringify({ realtime_user_id: newRealtimeUserId }));
        } catch (e) { /* best-effort, ignore */ }
    }
};

// State for unified search
const searchText = ref('');
const contactLoading = ref(false);
const contactResults = ref([]);
let debounceTimer = null;

onMounted(() => {
    shareModalStore.state.modal = modal.value;
});

// Helpers to identify contact type
const isTeam = (c) => {
    if (!c) return false;
    if (c.isTeam && c.isTeam === true) return true;
    return false;
};

const contactKey = (c) => c.id || c._id || c.contactId || JSON.stringify(c);
const contactLabel = (c) => {
    if (!c) return '';
    if (isTeam(c)) return c.name || c.teamName || c._id;
    return c.name ? `${c.name} (${c.email || ''})` : (c.email || c.id || 'Unknown');
};

const contactNameOnly = (c) => {
    if (!c) return '';
    if (isTeam(c)) return c.name || c.teamName || 'Team';
    return c.name || c.email || 'User';
};

const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) {
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
};

// Filter results against already shared entities
const filteredContactResults = computed(() => {
    return contactResults.value.filter(contact => {
        if (!contact) return false;
        if (isTeam(contact)) {
            const id = contact.id || contact._id || contact.teamId || '';
            if (!id) return false;
            if (shareModalStore.state.sharedTeams.some(team => (team.id || team._id) === id)) return false;
            return true;
        } else {
            const id = contact.id || contact._id || contact.email || '';
            if (!id) return false;
            if (shareModalStore.state.sharedUsers.some(user => (user.id || user._id) === id)) return false;
            return true;
        }
    });
});

const contactErrorMessage = computed(() => {
    if (filteredContactResults.value.length === 0 && searchText.value && !contactLoading.value && searchText.value.length >= 2) {
        return 'No contacts found or already shared';
    }
    return '';
});

// Fetch contacts from unified endpoint (same as EventParticipantSearch)
const fetchContacts = async (term) => {
    contactLoading.value = true;
    try {
        const encoded = encodeURIComponent(term);
        const resp = await fetch(`/api/members/contacts?term=${encoded}`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' }
        });
        if (resp.ok) {
            const data = await resp.json();
            if (Array.isArray(data)) {
                contactResults.value = data;
            } else {
                contactResults.value = [];
            }
        } else {
            console.error('Unexpected response searching contacts', resp.status);
            contactResults.value = [];
            toastStore.dispatch('addMessage', { message: 'Failed to search contacts', icon: 'error', ttl: 5000 });
        }
    } catch (err) {
        console.error('Error fetching contacts', err);
        contactResults.value = [];
        toastStore.dispatch('addMessage', { message: 'Failed to search contacts', icon: 'error', ttl: 5000 });
    } finally {
        contactLoading.value = false;
    }
};

// Watcher with debounce to trigger search when user types at least 2 chars
watch(searchText, (val) => {
    if (debounceTimer) clearTimeout(debounceTimer);
    if (!val || val.length < 2) {
        contactResults.value = [];
        contactLoading.value = false;
        return;
    }
    debounceTimer = setTimeout(() => {
        fetchContacts(val.trim());
    }, 300);
});

const handleAddContact = (contact) => {
    if (isTeam(contact)) {
        handleTeamSelected(contact);
    } else {
        handleUserSelected(contact);
    }
    // Clear search after adding
    searchText.value = '';
    contactResults.value = [];
};

const updateRole = (newRole) => {
    shareModalStore.dispatch("updateRole", newRole);
};

const generateShareLink = () => {
    shareModalStore.dispatch("generateShareLink");
};

const copyToClipboard = async () => {
    try {
        await navigator.clipboard.writeText(shareModalStore.state.shareLink);
        shareModalStore.commit("SET_ERROR", { 
            error: false, 
            message: "Link copied to clipboard!" 
        });
    } catch (err) {
        shareModalStore.commit("SET_ERROR", { 
            error: true, 
            message: "Failed to copy link" 
        });
    }
};

const removeUser = (userId) => {
    shareModalStore.dispatch("removeUser", userId).then(() => {
        if (!shareModalStore.state.error) {
            shareModalStore.dispatch("loadSharedEntities");
        }
    });
};

const handleTeamSelected = (team) => {
    shareModalStore.dispatch("shareWithTeam", team._id).then(() => {
        if (!shareModalStore.state.error) {
            shareModalStore.dispatch("loadSharedEntities");
        }
    });
};

const handleUserSelected = (user) => {
    shareModalStore.dispatch("shareWithUser", user._id || user.id).then(() => {
        if (!shareModalStore.state.error) {
            shareModalStore.dispatch("loadSharedEntities");
        }
    });
};

const removeTeam = (teamId) => {
    shareModalStore.dispatch("removeTeam", teamId).then(() => {
        if (!shareModalStore.state.error) {
            shareModalStore.dispatch("loadSharedEntities");
        }
    });
};

defineExpose({ modal });
</script>